(() => {
  'use strict';
  // The editor may re-run asset scripts; one registry and abortable listeners prevent duplicates.
  if (window.WotfRitualCollections) { window.WotfRitualCollections.init(document); return; }
  const instances = new Map();
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function initRoot(root) {
    if (instances.has(root)) return;
    const lifecycle = new AbortController();
    const signal = lifecycle.signal;
    const grid = root.querySelector('[data-collection-grid]');
    const cards = [...root.querySelectorAll('[data-collection-card]')];
    const limit = [12,24,48].includes(Number(root.dataset.limit)) ? Number(root.dataset.limit) : 24;
    const states = cards.map(card => {
      const trigger = card.querySelector('[data-collection-toggle]');
      const panel = root.querySelector('#' + CSS.escape(trigger.dataset.panel));
      const rail = panel.querySelector('[data-product-rail]');
      rail.id = panel.id + '-rail';
      if (!trigger.hasAttribute('data-unconfigured')) {
        trigger.setAttribute('role','button');
        trigger.setAttribute('aria-expanded','false');
        trigger.setAttribute('aria-label','Explore '+card.querySelector('h3').textContent+' collection');
        trigger.setAttribute('aria-controls',panel.id);
      }
      return {card,trigger,panel,rail,ids:new Set(),next:null,total:null,loaded:false,pending:null,failed:false,scroll:0,requested:new Set()};
    });
    let active = null;
    let rangeTimer = 0;
    function endpoint(state, next) {
      const base = new URL(state.trigger.dataset.url, location.origin);
      const url = new URL(next || base.href, location.origin);
      if (url.origin !== location.origin || url.pathname !== base.pathname) throw new Error('Unexpected collection page');
      url.searchParams.set('view','shop');
      url.searchParams.set('section_id','home-ritual-products');
      return url.href;
    }
    function position() {
      if (!active) return;
      const columns = Number(getComputedStyle(root).getPropertyValue('--rs-columns')) || 4;
      const index = cards.indexOf(active.card);
      const nextCard = cards[(Math.floor(index / columns) + 1) * columns];
      const saved = active.rail.scrollLeft;
      if (nextCard && active.panel.nextElementSibling !== nextCard) grid.insertBefore(active.panel,nextCard); else if (!nextCard && grid.lastElementChild !== active.panel) grid.append(active.panel);
      active.rail.scrollLeft = saved || active.scroll;
      update(active);
    }
    function update(state) {
      if (state.panel.hidden) return;
      const overflow = state.rail.scrollWidth - state.rail.clientWidth;
      const controls = state.panel.querySelector('[data-slider-controls]');
      controls.hidden = overflow < 3;
      state.panel.querySelector('[data-prev]').disabled = state.rail.scrollLeft < 3;
      state.panel.querySelector('[data-next]').disabled = state.rail.scrollLeft >= overflow - 3 && (!state.next || state.ids.size >= limit || !!state.pending);
      const rect = state.rail.getBoundingClientRect();
      const products = [...state.rail.querySelectorAll('[data-product-id]')];
      const visible = products.map((p,i)=>({r:p.getBoundingClientRect(),i})).filter(p=>p.r.right > rect.left+3 && p.r.left < rect.right-3);
      const range = visible.length ? (visible[0].i+1)+'–'+(visible.at(-1).i+1) : '';
      state.panel.querySelector('[data-range]').textContent = range && state.total !== null ? range+' of '+state.total : '';
    }
    function close(focus) {
      if (!active) return;
      const state = active;
      state.scroll = state.rail.scrollLeft;
      state.panel.hidden = true;
      state.card.classList.remove('is-selected');
      state.trigger.setAttribute('aria-expanded','false');
      state.trigger.setAttribute('aria-label','Explore '+state.card.querySelector('h3').textContent+' collection');
      state.trigger.querySelector('[data-toggle-label]').textContent = 'Explore collection';
      state.trigger.querySelector('[data-toggle-sign]').textContent = '+';
      active = null;
      if (focus) state.trigger.focus({preventScroll:true});
    }
    function skeletons(state) {
      if (state.ids.size) return;
      state.rail.replaceChildren();
      for(let i=0;i<8;i++) {
        const card = document.createElement('div');
        card.className = 'home-ritual__skeleton';
        card.setAttribute('aria-hidden','true');
        state.rail.append(card);
      }
    }
    async function load(state) {
      if (state.pending) return state.pending;
      if (state.loaded && (!state.next || state.ids.size >= limit)) return;
      const url = endpoint(state,state.next);
      if (state.requested.has(url)) return;
      state.failed = false;
      state.panel.querySelector('[data-retry]').hidden = true;
      state.panel.querySelector('[data-load-status]').textContent = state.ids.size ? 'Loading more objects…' : 'Loading objects…';
      state.rail.setAttribute('aria-busy','true');
      skeletons(state);
        state.pending = (async () => {
        try {
          const response = await fetch(url,{credentials:'same-origin',signal:AbortSignal.any([signal,AbortSignal.timeout(15000)]),headers:{'Accept':'text/html'}});
          if (!response.ok) throw new Error('Unable to load products');
          const doc = new DOMParser().parseFromString(await response.text(),'text/html');
          const batch = doc.querySelector('[data-ritual-products]');
          if (!batch || batch.dataset.collectionId !== state.trigger.dataset.collectionId) throw new Error('Collection mismatch');
          const rawTotal = Number(batch.dataset.total);
          state.total = Number.isFinite(rawTotal) && rawTotal >= 0 ? rawTotal : null;
          const items = [...batch.querySelectorAll('[data-product-id]')];
          if (!state.ids.size) state.rail.replaceChildren();
          for (const item of items) {
            if (state.ids.size >= limit) break;
            if (!item.dataset.productId || state.ids.has(item.dataset.productId)) continue;
            item.querySelectorAll('script').forEach(script=>script.remove());
            state.ids.add(item.dataset.productId);
            state.rail.append(document.importNode(item,true));
          }
          const next = batch.dataset.next || null;
          if (next && endpoint(state,next) === url) throw new Error('Repeated collection page');
          state.requested.add(url);
          state.next = next;
          state.loaded = true;
          if (!state.ids.size) state.panel.querySelector('[data-load-status]').textContent = 'No objects in this collection yet. Explore the full collection for updates.';
          else state.panel.querySelector('[data-load-status]').textContent = '';
          if (state.ids.size >= limit && (state.next || state.total > state.ids.size)) {
            state.next = null;
            if (!state.rail.querySelector('.home-ritual__full-collection')) {
              const link = document.createElement('a');
              link.className = 'home-ritual__full-collection';
              link.href = state.panel.querySelector('.home-ritual__view-all').href;
              link.textContent = 'Explore the full collection →';
              link.setAttribute('role','listitem');
              state.rail.append(link);
            }
          }
          state.rail.scrollLeft = state.scroll;
        } catch(error) {
          if (error.name === 'AbortError') return;
          if (!state.ids.size) state.rail.replaceChildren();
          state.failed = true;
          state.panel.querySelector('[data-load-status]').textContent = 'Objects could not load. Try again or view the full collection.';
          state.panel.querySelector('[data-retry]').hidden = false;
        } finally {
          state.pending = null;
          state.rail.removeAttribute('aria-busy');
          update(state);
        }
      })();
      return state.pending;
    }
    function open(state) {
      if (active === state) { close(false); return; }
      close(false);
      active = state;
      state.panel.hidden = false;
      state.card.classList.add('is-selected');
      state.trigger.setAttribute('aria-expanded','true');
      state.trigger.setAttribute('aria-label','Close '+state.card.querySelector('h3').textContent+' collection');
      state.trigger.querySelector('[data-toggle-label]').textContent = 'Close collection';
      state.trigger.querySelector('[data-toggle-sign]').textContent = '−';
      position();
      state.rail.scrollLeft = state.scroll;
      load(state);
    }
    root.addEventListener('click',event => {
      const state = states.find(s=>s.card.contains(event.target) || s.panel.contains(event.target));
      if (!state) return;
      if (state.card.contains(event.target)) {
        if (state.trigger.hasAttribute('data-unconfigured')) return;
        event.preventDefault();
        open(state);
      } else if (event.target.closest('[data-close]')) close(true);
      else if (event.target.closest('[data-retry]')) load(state);
      else if (event.target.closest('[data-prev],[data-next]')) {
        const direction = event.target.closest('[data-prev]') ? -1 : 1;
        const advance = () => state.rail.scrollBy({left:direction*(state.rail.clientWidth+20),behavior:reduceMotion()?'instant':'smooth'});
        if (direction > 0 && state.rail.scrollLeft >= state.rail.scrollWidth-state.rail.clientWidth-3 && state.next && !state.failed) load(state).then(advance);
        else advance();
      }
    },{signal});
    root.addEventListener('keydown',event => {
      const state = states.find(s=>s.trigger===event.target);
      if (state && event.key===' ' && !state.trigger.hasAttribute('data-unconfigured')) {event.preventDefault();open(state);}
      if (event.key==='Escape' && active && active.panel.contains(event.target)) {event.preventDefault();close(true);}
    },{signal});
    states.forEach(state => state.rail.addEventListener('scroll',()=>{
      if (state.panel.hidden) return;
      state.scroll = state.rail.scrollLeft;
      clearTimeout(rangeTimer);
      rangeTimer = setTimeout(()=>update(state),80);
      const remaining = state.rail.scrollWidth-state.rail.clientWidth-state.rail.scrollLeft;
      if (remaining < state.rail.clientWidth*.45 && state.next && !state.pending && !state.failed) load(state);
    },{passive:true,signal}));
    const observer = new ResizeObserver(position);
    observer.observe(root);
    instances.set(root,()=>{lifecycle.abort();observer.disconnect();clearTimeout(rangeTimer);instances.delete(root);});
  }
  function init(scope) {
    if (scope.matches?.('[data-home-ritual]')) initRoot(scope);
    scope.querySelectorAll?.('[data-home-ritual]').forEach(initRoot);
  }
  window.WotfRitualCollections={init};
  init(document);
  document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    for(const [root,destroy] of instances) if(event.target===root || event.target.contains(root)) destroy();
  });
})();
